import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Search, ShieldCheck, XCircle, CircleCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EcosystemRegistry() {
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  // A signed-in Founder/Director mid-onboarding is routed to their own eKYC — never the public registry.
  if (user && (user.role === "director" || user.account_type === "director") && user.director_status !== "active") {
    return <Navigate to="/director/kyc" replace />;
  }

  const verify = async (e) => {
    e && e.preventDefault();
    const q = (code || "").trim();
    if (!q) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await fetch(`${API}/api/registry/verify/${encodeURIComponent(q)}`);
      if (r.status === 404) { setError("No active member found for that Vinci Code."); }
      else if (!r.ok) { setError("Verification service is unavailable. Try again later."); }
      else { setResult(await r.json()); }
    } catch { setError("Network error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#050506] text-[#F5F5F0]">
      <Header />
      <main className="max-w-4xl mx-auto px-6 md:px-12 pt-28 pb-24">
        <div className="border-b border-white/10 pb-6 mb-8">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#22D3EE]">Ecosystem Registry</div>
          <h1 className="font-serif text-4xl md:text-5xl mt-2">Verify any Vinci partner in one code.</h1>
          <p className="font-body text-sm text-[#8A8A82] mt-3 max-w-2xl">
            Enter the 5151-series Vinci Code shown on any Vinci-issued invoice, certificate, board voucher or business card. This registry is public — anyone (buyer, seller, government officer, bank) can validate the partner's live status, GST registration, and bond compliance.
          </p>
        </div>

        <form onSubmit={verify} className="flex items-center gap-3 flex-wrap" data-testid="registry-form">
          <div className="relative flex-1 min-w-[280px]">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#22D3EE]"/>
            <input data-testid="registry-input" autoFocus value={code}
                   onChange={(e) => setCode(e.target.value.toUpperCase())}
                   placeholder="VNC-5151-####  ·  or  ·  5151-####"
                   className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#22D3EE] rounded-sm pl-11 pr-4 py-3 font-mono text-lg tracking-[0.15em] text-[#F5F5F0] focus:outline-none"/>
          </div>
          <button type="submit" disabled={busy || !code.trim()} data-testid="registry-submit"
                  className="rounded-sm px-6 py-3 font-grotesk text-[10px] uppercase tracking-[0.28em] bg-[#22D3EE] text-black disabled:opacity-40">
            {busy ? "Checking…" : "Verify"}
          </button>
        </form>

        {error && (
          <div data-testid="registry-error"
               className="mt-6 border border-[#C0392B]/40 bg-[#C0392B]/[0.06] rounded-sm p-4 flex items-center gap-3">
            <XCircle size={18} className="text-[#C0392B]"/>
            <span className="font-body text-sm text-[#F5F5F0]">{error}</span>
          </div>
        )}

        {result && (
          <div data-testid="registry-card"
               className="mt-8 border border-white/10 rounded-sm bg-[#0a0a0a] p-6 space-y-4"
               style={{ boxShadow: "0 0 80px rgba(34,211,238,0.15)" }}>
            <div className="flex items-start gap-4 border-b border-white/[0.08] pb-4">
              <div className="h-14 w-14 rounded-sm bg-[#22D3EE]/15 text-[#22D3EE] grid place-items-center">
                <ShieldCheck size={26}/>
              </div>
              <div>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#22D3EE]">Verified partner</div>
                <div className="font-serif text-3xl text-[#F5F5F0] mt-1">{result.business_name}</div>
                <div className="font-body text-[12px] text-[#8A8A82] mt-1">{result.vinci_code} · {result.category?.replace(/_/g," ")}</div>
              </div>
              <div className="ml-auto text-right">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#2FB999]/40 bg-[#2FB999]/[0.08] text-[#2FB999] font-grotesk text-[10px] uppercase tracking-[0.2em]">
                  <CircleCheck size={12}/> {result.status_pct}% Active
                </div>
                {result.verified_since && (
                  <div className="mt-2 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">
                    Verified since {new Date(result.verified_since).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}
                  </div>
                )}
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { k: "Verification status", v: `${result.status_pct}% Active`, ok: result.status_pct >= 100 },
                { k: "GST", v: result.gst_status, ok: result.gst_status === "Validated" },
                { k: "Bond", v: result.bond_status, ok: result.bond_status === "Secured" },
              ].map(x => (
                <div key={x.k} data-testid={`registry-${x.k.toLowerCase().split(' ')[0]}`}
                     className="border border-white/[0.08] rounded-sm p-3">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">{x.k}</div>
                  <div className={`font-serif text-lg mt-1 ${x.ok ? "text-[#2FB999]" : "text-[#D8B23E]"}`}>{x.v}</div>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-white/[0.06] font-body text-[11px] text-[#8A8A82]">
              This registry is a public read-only lookup. For dispute enquiries or claim
              disagreements, contact <a href="mailto:vinci.connect@vincitradeasia.com" className="text-[#22D3EE] underline underline-offset-4">vinci.connect@vincitradeasia.com</a>.
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
